import { useCallback, useEffect, useState, useContext } from "react"
import { DateTime } from "luxon"

//PrimeReact
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Tooltip } from "primereact/tooltip"

//Helpers:
import { LogError, LogWarning } from "../../../helpers/logger"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//SOAR Components
import { LoaderMedium } from "../../../components/Loaders/index"

//Housing components
import { housingApiListRentPost } from "../housingApi"
import HousingRentFilter from "./HousingRentFilter"
import myStyle from "../../../styles/housing/HousingRent.module.css"
import HousingRentPaymentForm from "./HousingRentPaymentForm"
import HousingOtherChargeForm from "./HousingOtherChargeForm"

export default function HousingRent({
  participant,
  onSetParticipant,
  houseParticipants,
  activeHouseParticipants,
  houses,
  users,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(true)
  const [rentEntries, setRentEntries] = useState([])
  const [pageSetup, setPageSetup] = useState({ rows: 20, first: 0, page: 0 })
  const [rentFilters, setRentFilters] = useState({
    status: "Due",
    houseId: "",
    fromDate: undefined,
    toDate: new Date(),
    participantId: undefined,
  })

  const [rentEntry, setRentEntry] = useState(undefined)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [userChargeEntry, setUserChargeEntry] = useState(undefined)
  const [showUserChargeForm, setShowUserChargeForm] = useState(false)
  const [expandedRows, setExpandedRows] = useState(null)

  const getRentEntries = useCallback(async () => {
    try {
      setIsLoading(true)

      const startDate = DateTime.fromJSDate(rentFilters.fromDate)
        .toUTC()
        .toISODate()

      const endDate = DateTime.fromJSDate(rentFilters.toDate)
        .set({ hour: 23 })
        .set({ minute: 59 })
        .set({ second: 59 })
        .toUTC()
        .toISO()

      const result = await housingApiListRentPost(userCtx.tokenHousing, {
        participantId: rentFilters.participantId,
        enterpriseId: userCtx.session.user.enterprise_id,
        organizationId: userCtx.session.user.organization_id,
        statusFilter: rentFilters.status,
        houseId: rentFilters.houseId,
        start: startDate,
        end: endDate,
        PageSize: pageSetup.rows,
        PageNum: pageSetup.page + 1,
      })

      if (result.length > 0) {
      }

      setRentEntries(result)

      setIsLoading(false)
    } catch (error) {
      LogError("Unable to retrieve Rent entry list", error)
      userCtx.setNotificationError("Unable to retrieve rent entries")
      setRentEntries([])
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rentFilters.fromDate,
    rentFilters.toDate,
    rentFilters.participantId,
    rentFilters.status,
    rentFilters.houseId,
    userCtx.tokenHousing,
    userCtx.session.user.enterprise_id,
    userCtx.session.user.organization_id,
    pageSetup.rows,
    pageSetup.page,
    userCtx.setNotificationError,
  ])

  useEffect(() => {
    getRentEntries()
  }, [getRentEntries])

  const formatDate = (value) => {
    let date = value

    if (!!date) {
      date = DateTime.fromISO(date, { zone: "utc" }).toLocal().toLocaleString()
    }

    return date
  }

  const DateColumnTemplate = (rowData) => {
    const today = DateTime.now()
    let date = rowData.rent.dueDate
    let graceDate = rowData.graceEndDate
    let showGraceTimer = false

    if (!!date) {
      date = DateTime.fromISO(date, { zone: "utc" }).toLocal()
    }

    if (!!graceDate) {
      graceDate = DateTime.fromISO(graceDate, { zone: "utc" }).toLocal()

      if (date < today && graceDate >= today) {
        showGraceTimer = true
      }
    }

    return (
      <div
        className="house-rent-t-tip"
        data-pr-tooltip={
          showGraceTimer
            ? `This rent entry has grace to be paid until ${graceDate.toLocaleString()}`
            : ""
        }
      >
        {date.toLocaleString()}{" "}
        {showGraceTimer ? (
          <span className={`material-icons ${myStyle.rentGrace}`}>
            {"schedule"}
          </span>
        ) : null}
      </div>
    )
  }

  const StatusColumnBody = (rowData) => {
    let color = "gray"

    switch (rowData.status) {
      case "Due":
        color = "Black"
        break
      case "Overdue":
        color = "red"
        break
      case "Paid":
        color = "green"
        break
      default:
        break
    }

    return <div style={{ color: color }}>{rowData.status}</div>
  }

  const ParticipantColumnBody = (rowData) => {
    let name = "Unknown"
    if (!!houseParticipants) {
      const rowParticipant = houseParticipants.filter(
        (participant) =>
          participant.participant_id === rowData.rent.participantId
      )

      if (!!rowParticipant && rowParticipant.length > 0) {
        name = rowParticipant[0].name2
      }
    }

    return <>{name}</>
  }

  const formatCurrency = (value) => {
    return value.toLocaleString("en-US", { style: "currency", currency: "USD" })
  }

  const RentColumnBody = (rowData) => {
    return <>{formatCurrency(rowData.rent.amountDue || 0)}</>
  }

  const ReceiptColumnBody = (rowData) => {
    return <>{formatCurrency(rowData.paymentsReceived || 0)}</>
  }

  const ActionColumnBody = (rowData) => {
    const participant = houseParticipants.find(
      (participant) => participant.participant_id === rowData.rent.participantId
    )

    return (
      <div style={{ display: "flex" }}>
        <Tooltip target=".house-rent-t-tip" />
        <button
          className={`btn ${myStyle.filterHeaderButton} house-rent-t-tip`}
          data-pr-tooltip="Create a receipt entry for this rent item"
          onClick={() => {
            setRentEntry(rowData)
            setShowPaymentForm(true)
          }}
          disabled={!participant?.statusActive}
        >
          <span className={`material-icons`}>{"attach_money"}</span>
        </button>
        {!rowData.rent.isRent ? (
          <button
            className={`btn ${myStyle.filterHeaderButton} house-rent-t-tip`}
            data-pr-tooltip="Edit the rent item"
            onClick={() => {
              setUserChargeEntry(rowData)
              setShowUserChargeForm(true)
            }}
            disabled={!participant?.statusActive}
          >
            <span className={`material-icons`}>{"edit"}</span>
          </button>
        ) : null}
      </div>
    )
  }

  const allowExpansion = (rowData) => {
    return rowData.payments.length > 0
  }

  const rowExpansionTemplate = (data) => {
    if (data.payments.length > 0) {
      return (
        <div>
          <div className={myStyle.expandedHeader}>Receipts</div>
          <DataTable value={data.payments}>
            <Column
              body={(rowData) => {
                return <>{formatDate(rowData.date)}</>
              }}
              header="Date"
              sortable
              style={{ width: "80px" }}
            ></Column>
            <Column header="Staff" body={ReceiptUserColumnTemplate} />
            <Column
              body={(rowData) => {
                return <>{formatCurrency(rowData.amountPaid)}</>
              }}
              header="Amount"
              sortable
              style={{ width: "100px" }}
            ></Column>
            <Column
              field="note"
              header="Notes"
              style={{ minWidth: "150px" }}
            ></Column>
          </DataTable>
        </div>
      )
    } else {
      return <div className={myStyle.expandedHeader}>No receipts available</div>
    }
  }

  const TypeColumnTemplate = (rowData) => {
    let type = "Rent"

    if (!rowData.rent.isRent) {
      type = rowData.rent.description
    }

    return <>{type}</>
  }

  const UserColumnTemplate = (rowData) => {
    try {
      let userName = "Unknown"

      const user = users?.find((user) => user.staff_id === rowData.rent.userId)

      if (!!user) {
        userName = `${user.staff_name_first} ${user.staff_name_last}`
      }

      return <>{userName}</>
    } catch (error) {
      LogError("Unable to retrieve Bed allocation staff member", error)
      return <></>
    }
  }

  const ReceiptUserColumnTemplate = (rowData) => {
    try {
      let userName = rowData.userId

      const user = users?.find((user) => user.staff_id === userName)

      if (!!user) {
        userName = `${user.staff_name_first} ${user.staff_name_last}`
      } else if (rowData.userId === userCtx.session.user.staff_id) {
        //Get current user details
        userName = `${userCtx.session.user.staff_name_first} ${userCtx.session.user.staff_name_last}`
      } else {
        LogWarning(`Unable to find user: ${rowData.userId}`)
        userName = "Unknown"
      }

      return <>{userName}</>
    } catch (error) {
      LogError("Unable to retrieve Bed allocation staff member", error)
      return <></>
    }
  }

  return (
    <>
      <HousingRentPaymentForm
        showForm={showPaymentForm}
        onHideForm={() => {
          setShowPaymentForm(false)
        }}
        onPaymentCreated={() => {
          getRentEntries()
          setShowPaymentForm(false)
        }}
        rentEntry={rentEntry}
        participants={houseParticipants}
      />
      <HousingOtherChargeForm
        showForm={showUserChargeForm}
        onHideForm={() => {
          setShowUserChargeForm(false)
          setUserChargeEntry(undefined)
        }}
        userChargeEntry={userChargeEntry}
        houseList={houses}
        houseParticipantList={houseParticipants}
        activeHouseParticipants={activeHouseParticipants}
        onChargeCreated={() => {
          getRentEntries()
          setShowUserChargeForm(false)
          setUserChargeEntry(undefined)
        }}
      />
      <HousingRentFilter
        participants={houseParticipants}
        participant={participant}
        onSetParticipant={onSetParticipant}
        isLoading={isLoading}
        houseList={houses}
        onRefreshClick={getRentEntries}
        onChangeFilters={(filters) => {
          setRentFilters(filters)
        }}
        onNewChargeClick={() => {
          setShowUserChargeForm(true)
        }}
      />
      {isLoading ? (
        <LoaderMedium />
      ) : (
        <DataTable
          value={rentEntries.rentSchedule}
          lazy
          paginator
          first={(rentEntries.pageNum - 1) * rentEntries.pageSize}
          rows={rentEntries.pageSize}
          totalRecords={rentEntries.numTotal}
          onPage={(setup) => {
            setPageSetup(setup)
          }}
          disabled={isLoading}
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            setExpandedRows(e.data)
          }}
          rowExpansionTemplate={rowExpansionTemplate}
          rowClassName={(rowData) =>
            rowData.payments.length === 0 ? myStyle.disableExpander : ""
          }
        >
          <Column
            expander={allowExpansion}
            style={{ width: "5rem" }}
            disabled={allowExpansion}
            header="Receipts"
          />
          <Column body={TypeColumnTemplate} header="Type" />
          <Column
            body={DateColumnTemplate}
            header="Due date"
            style={{ width: "125px" }}
          />
          <Column header="Staff" body={UserColumnTemplate} />
          <Column field={"rent.house.name"} header="House" />

          <Column body={ParticipantColumnBody} header="Participant" />

          <Column
            body={RentColumnBody}
            header="Amount"
            style={{ width: "75px" }}
          />
          <Column
            body={ReceiptColumnBody}
            header="Receipts"
            style={{ width: "75px" }}
          />
          <Column
            body={StatusColumnBody}
            header="Status"
            style={{ width: "75px" }}
          />
          <Column body={ActionColumnBody} style={{ width: "50px" }} />
        </DataTable>
      )}
    </>
  )
}
